import { useCallback, useEffect, useState } from "react";
import {
	useGetUserSettingsQuery,
	usePutUserSettingsApplicationByApplicationNameMutation,
} from "src/datahandling/redux/api/rtkQueryCodeGen/customer_settingsApi/customer_settingsApiInjections";
import { APP_CONFIG, DEFAULT_USER_SETTINGS } from "src/models/constants";
import type { UserSettings } from "src/models/types/userSettings.types";
import { useAuthorization } from "./useAuthorization";

export function useUserSettingsConfig() {
	const { isAuthorized } = useAuthorization();
	const { data, isLoading, isFetching } = useGetUserSettingsQuery({}, { skip: !isAuthorized });
	const [config, setConfig] = useState<UserSettings>(DEFAULT_USER_SETTINGS);
	const [isInitialized, setIsInitialized] = useState(false);
	const [update] = usePutUserSettingsApplicationByApplicationNameMutation();

	const _updateConfig = useCallback((newConfig: UserSettings) => {
		setConfig((prevConfig) => {
			if (!prevConfig && newConfig) {
				return newConfig;
			}
			if (prevConfig && newConfig && JSON.stringify(prevConfig) !== JSON.stringify(newConfig)) {
				return newConfig;
			}
			return prevConfig;
		});
	}, []);

	useEffect(() => {
		if (data) {
			// Check if applicationSpecific exists and has settings for this app
			const hasAppSettings =
				data.applicationSpecific?.[APP_CONFIG.appId as keyof typeof data.applicationSpecific];

			if (!hasAppSettings) {
				const { applicationSpecific, ...rest } = data;
				console.warn("No app specific settings found, initializing defaults.");

				// Initialize with defaults
				const initialSettings: UserSettings = {
					...rest,
					applicationSpecific: {
						...applicationSpecific,
						...DEFAULT_USER_SETTINGS.applicationSpecific,
					},
				};

				// Update backend with initial settings
				update({
					applicationName: APP_CONFIG.appId,
					body: initialSettings.applicationSpecific?.[APP_CONFIG.appId],
				})
					.unwrap()
					.then(() => {
						_updateConfig(initialSettings);
						setIsInitialized(true);
					})
					.catch((error) => {
						console.error("Failed to initialize default settings:", error);
						// Still set local config even if backend update fails
						_updateConfig(initialSettings);
						setIsInitialized(true);
					});
			} else {
				_updateConfig(data as UserSettings);
				setIsInitialized(true);
			}
		}
	}, [data, update, _updateConfig]);

	const setUserSettings = useCallback(
		async (newConfig: UserSettings) => {
			if (!isAuthorized) {
				throw new Error("User not authorized");
			}

			try {
				const result = await update({
					applicationName: APP_CONFIG.appId,
					body: newConfig.applicationSpecific?.[APP_CONFIG.appId],
				}).unwrap();

				_updateConfig(newConfig);
				return result;
			} catch (error) {
				console.error("Failed to set user settings:", error);
				throw error;
			}
		},
		[update, isAuthorized, _updateConfig],
	);

	return {
		userSettings: config,
		setUserSettings,
		isLoading: isLoading || isFetching || !isInitialized,
		isInitialized,
	};
}
